import ApproversUpsellSvg from 'ext-ui/svg/illustration/ApproversUpsellSvg'
import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import LayoutPage from 'src/component-layout/LayoutPage/LayoutPage'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import CardApprovalsBox from 'src/components/CardApprovalsBox/CardApprovalsBox'
import HorizontalContentCollection from 'src/components/HorizontalContentCollection/HorizontalContentCollection'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import OrgSettingsOutOfPocketExpensesBox from 'src/components/OrgSettingsOutOfPocketExpensesBox/OrgSettingsOutOfPocketExpensesBox'
import OrgSettingsReceiptRulesBox from 'src/components/OrgSettingsReceiptRulesBox/OrgSettingsReceiptRulesBox'
import VerticalContentCollection from 'src/components/VerticalContentCollection/VerticalContentCollection'
import SaasUpgradeOrContent from 'src/feature/SaasUpgradeOrContent/SaasUpgradeOrContent'
import { useBackgroundDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import {
  getOrganization,
  getOrganizationCardApprovalsEnabled,
  getOrganizationCurrencies,
} from 'src/redux/actions/data/organizations.actions'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import {
  selectCardApprovalsEnabled,
  selectCardApprovalsFromState,
  selectOrganizationIdFromRoute,
  selectOutOfPocketExpensesEnabledFromState,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

const BusinessRules: React.FC = () => {
  const user = useSelector(selectUserListModelFromState)
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const organizationId = useSelector(selectOrganizationIdFromRoute)
  const outOfPocketExpensesEnabled = useSelector(
    selectOutOfPocketExpensesEnabledFromState,
  )
  const cardApprovalsEnabled = useSelector(selectCardApprovalsEnabled)
  const cardApprovalRules = useSelector(selectCardApprovalsFromState)
  const appName = useSelector(selectAppName)

  const dispatch = useBackgroundDispatch()
  const translate = useTranslate()

  const { show_new_org_settings: showNewOrgSettings } = useSelector(
    selectFeatureFlaggerFromState,
  )

  const { VIEW_VIRTUAL_CARD_FEATURES: hasVirtualCardFeatures } =
    useQueryUserFeatures([UserFeature.VIEW_VIRTUAL_CARD_FEATURES])?.data || {}

  useEffect(() => {
    dispatch(getOrganization(organizationId, true))
  }, [])

  useEffect(() => {
    if (organization?.currencies === undefined) {
      dispatch(getOrganizationCurrencies(organizationId))
    }
  }, [organization?.currencies])

  useEffect(() => {
    if (cardApprovalRules === undefined) {
      dispatch(getOrganizationCardApprovalsEnabled(organizationId))
    }
  }, [cardApprovalRules])

  if (
    user === undefined ||
    !user.hasOrgBookkeeperPermissions ||
    !organization
  ) {
    return <NotFoundBackgroundPageContent />
  }

  if (!showNewOrgSettings) {
    return (
      <>
        <Helmet>
          <title>{`Business Rules | ${appName}`}</title>
          <meta
            name="description"
            content={`Business Rules page of ${appName}`}
          />
        </Helmet>
        <BackgroundPageContent>
          {outOfPocketExpensesEnabled ? (
            <HorizontalContentCollection splitCenter>
              <OrgSettingsReceiptRulesBox
                organization={organization}
                user={user}
              />
              <VerticalContentCollection>
                <VerticalContentCollection>
                  <OrgSettingsOutOfPocketExpensesBox
                    organization={organization}
                    user={user}
                  />
                  {hasVirtualCardFeatures && (
                    <CardApprovalsBox
                      organization={organization}
                      cardApprovalRules={cardApprovalRules}
                      cardApprovalsEnabled={cardApprovalsEnabled}
                      user={user}
                    />
                  )}
                </VerticalContentCollection>
              </VerticalContentCollection>
            </HorizontalContentCollection>
          ) : (
            <HorizontalContentCollection splitCenter>
              <OrgSettingsReceiptRulesBox
                organization={organization}
                user={user}
              />
              {hasVirtualCardFeatures && (
                <CardApprovalsBox
                  organization={organization}
                  cardApprovalRules={cardApprovalRules}
                  cardApprovalsEnabled={cardApprovalsEnabled}
                  user={user}
                />
              )}
            </HorizontalContentCollection>
          )}
        </BackgroundPageContent>
      </>
    )
  }

  return (
    <>
      <Helmet>
        <title>{`Approvers | ${appName}`}</title>
        <meta name="description" content={`Approvers page of ${appName}`} />
      </Helmet>
      {/* TODO: make sure we update to address and use `hasVirtualCardFeatures` */}
      <LayoutPage padding={'none'}>
        <SaasUpgradeOrContent
          illustration={<ApproversUpsellSvg />}
          title={translate('Approvers')}
          description={translate(
            'Enlist help managing finance operations across your organization. Assign an approver for card requests and an approver for reimbursements.',
          )}
          ctaDetailSrc={
            'Upgrade to ${tier} today and delegate approval responsibilities to your team.'
          }
          feature={UserFeature.BUSINESS_RULES}
        >
          <LayoutPage title={<span>{translate('Approvers')}</span>}>
            <div
              css={css`
                max-width: 570px;
              `}
            >
              <CardApprovalsBox
                organization={organization}
                cardApprovalRules={cardApprovalRules}
                cardApprovalsEnabled={cardApprovalsEnabled}
                user={user}
              />
            </div>
          </LayoutPage>
        </SaasUpgradeOrContent>
      </LayoutPage>
    </>
  )
}

export default BusinessRules
